/**
 * « L'offre a été acceptée, le chantier est fini, la facture envoyée, mais le
 * chantier reste en cours » : le chantier ne passait à « Terminé » que par le
 * PV de réception. La facture finale d'une offre envoyée ou payée le termine,
 * même si elle est partie avant cette règle ou depuis un autre appareil.
 *
 * <p>Un chantier rouvert à la main est noté dans les réglages de la société
 * ({@link CLE_CHANTIERS_ROUVERTS}) : il ne se referme pas au chargement
 * suivant, seulement à l'envoi d'une nouvelle facture finale. Un acompte ou une
 * facture d'avancement ne termine rien.
 */

/** Réglage (JSON) : identifiants des chantiers rouverts à la main. */
export const CLE_CHANTIERS_ROUVERTS = 'chantiersRouverts';

/** Une facture qui n'est pas partie chez le client. */
const PAS_ENVOYEE = new Set(['Brouillon', 'Annulée']);

/**
 * @param {object} f facture
 * @returns {boolean} vrai pour la facture qui solde les travaux : ni acompte
 *   (ni facture d'avancement, qui en est un), ni avoir
 */
function estFinale(f) {
  const type = String(f?.type || 'standard').toLowerCase();
  return type !== 'acompte' && type !== 'avoir' && !f?.acompte;
}

/**
 * Les chantiers que vient de terminer l'envoi de leur facture finale.
 *
 * @param {Map<*, string>} statutsAvant statut de chaque facture au dernier
 *   passage (identifiant → statut) ; une facture absente vient d'être créée
 * @param {object[]} factures factures actuelles
 * @param {object[]} chantiers chantiers actuels
 * @returns {Array} identifiants des chantiers à terminer
 */
export function chantiersTerminesParLaFacture(statutsAvant, factures, chantiers) {
  const ids = new Set();
  for (const f of factures || []) {
    const avant = statutsAvant.get(f.id);
    if (avant !== undefined && avant !== 'Brouillon') continue;
    if (!f.statut || PAS_ENVOYEE.has(f.statut) || !estFinale(f)) continue;
    // Une facture sans offre (régie, petits travaux) ne dit pas que le
    // chantier est fini.
    if (f.devisId == null) continue;
    for (const ch of chantiers || []) {
      if (ch.statut === 'Terminé') continue;
      const lie =
        (f.siteId != null && String(ch.id) === String(f.siteId)) ||
        (ch.devisId != null && String(ch.devisId) === String(f.devisId));
      if (lie) ids.add(ch.id);
    }
  }
  return [...ids];
}

/**
 * Les chantiers dont la facture finale est déjà partie (envoyée, vue,
 * payée…) et qui ne sont pas terminés, hors chantiers rouverts à la main.
 *
 * @param {object[]} factures factures actuelles
 * @param {object[]} chantiers chantiers actuels
 * @param {Array} [rouverts] identifiants des chantiers rouverts à la main
 * @returns {Array} identifiants des chantiers à terminer
 */
export function chantiersFinisParLeurFacture(factures, chantiers, rouverts = []) {
  const garde = new Set((rouverts || []).map(String));
  return chantiersTerminesParLaFacture(new Map(), factures, chantiers).filter(
    (id) => !garde.has(String(id)),
  );
}

/**
 * @param {object} ch chantier
 * @param {string} aujourdhui date du jour, ISO
 * @returns {object} le chantier terminé : avancement à 100 %, date de fin
 *   gardée si elle est déjà posée
 */
export function termineLeChantier(ch, aujourdhui) {
  return { ...ch, statut: 'Terminé', av: 100, fin: ch.fin || aujourdhui };
}
